import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KnowledgeDocumentDTO, KnowledgeEntryDTO } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

type StatusFilter = "" | "active" | "archived";

/**
 * Knowledge Base — tenant-scoped FAQ corpus that backs the `faq_lookup`
 * action (ADR 0021). One row per Q&A pair; tags partition the corpus.
 *
 * The page is intentionally simple: table list, inline create/edit
 * dialog, server-side text-index search. Tags surface as chips and as
 * a multi-tag query param. No bulk import in MVP.
 */
export function KnowledgeBasePage() {
  const [entries, setEntries] = useState<KnowledgeEntryDTO[]>([]);
  const [status, setStatus] = useState<StatusFilter>("active");
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [editing, setEditing] = useState<KnowledgeEntryDTO | "new" | null>(null);
  const [confirmEntry, setConfirmEntry] = useState<KnowledgeEntryDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadEntries = useCallback(
    async (opts: { reset?: boolean } = {}) => {
      setBusy(true);
      setErr(null);
      try {
        const res = await api.agentflow.listKnowledgeEntries({
          status: status === "" ? undefined : status,
          // Document chunks are managed through the Documents section —
          // the Q&A table only shows hand-authored pairs.
          type: "qa",
          query: query.trim() || undefined,
          tags: activeTag ? [activeTag] : undefined,
          cursor: opts.reset ? undefined : (nextCursor ?? undefined),
          limit: 50,
        });
        setEntries((prev) => (opts.reset ? res.entries : [...prev, ...res.entries]));
        setNextCursor(res.nextCursor);
      } catch (e) {
        setErr(e instanceof ApiClientError ? e.message : "Failed to load entries.");
      } finally {
        setBusy(false);
      }
    },
    // We deliberately omit nextCursor to keep reset semantics simple.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, query, activeTag],
  );

  const loadTags = useCallback(async () => {
    try {
      const tags = await api.agentflow.listKnowledgeTags();
      setAllTags(tags);
    } catch {
      // Tag list is decorative — silent fail is acceptable.
    }
  }, []);

  useEffect(() => {
    void loadEntries({ reset: true });
    void loadTags();
  }, [loadEntries, loadTags]);

  async function handleSave(payload: {
    id?: string;
    question: string;
    answer: string;
    tags: string[];
    status: "active" | "archived";
  }) {
    try {
      if (payload.id) {
        const updated = await api.agentflow.updateKnowledgeEntry(payload.id, {
          question: payload.question,
          answer: payload.answer,
          tags: payload.tags,
          status: payload.status,
        });
        setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      } else {
        const created = await api.agentflow.createKnowledgeEntry({
          question: payload.question,
          answer: payload.answer,
          tags: payload.tags,
          status: payload.status,
        });
        setEntries((prev) => [created, ...prev]);
      }
      setEditing(null);
      void loadTags();
    } catch (e) {
      throw e instanceof ApiClientError ? e : new Error("Failed to save");
    }
  }

  async function handleDelete(id: string) {
    setDeleting(true);
    try {
      await api.agentflow.deleteKnowledgeEntry(id);
      setEntries((prev) => prev.filter((e) => e.id !== id));
      void loadTags();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to delete");
    } finally {
      setDeleting(false);
      setConfirmEntry(null);
    }
  }

  const tagCloud = useMemo(() => allTags, [allTags]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
            AgentFlow
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
            Knowledge Base
          </h1>
          <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
            Q&amp;A pairs and uploaded documents your agents can use to respond to customer
            questions. Add tags to scope entries to specific flows or topics.
          </p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          Add entry
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void loadEntries({ reset: true });
          }}
          placeholder="Search question or answer text…"
          className="h-9 w-72 max-w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="">All</option>
        </select>
        <Button variant="secondary" size="sm" onClick={() => void loadEntries({ reset: true })}>
          Refresh
        </Button>
      </div>

      {tagCloud.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Filter by tag">
          <span className="text-[11.5px] uppercase tracking-wider text-zinc-500">Tags:</span>
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={`rounded-full px-2.5 py-0.5 text-[11.5px] transition ${
              activeTag === null
                ? "bg-brand-600 text-white"
                : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
            }`}
          >
            All
          </button>
          {tagCloud.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTag(activeTag === t ? null : t)}
              className={`rounded-full px-2.5 py-0.5 text-[11.5px] transition ${
                activeTag === t
                  ? "bg-brand-600 text-white"
                  : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800/80 dark:bg-zinc-900/40">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11.5px] uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
            <tr>
              <th className="px-4 py-2.5 font-medium">Question</th>
              <th className="px-4 py-2.5 font-medium">Answer</th>
              <th className="px-4 py-2.5 font-medium">Tags</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
            {busy && entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-zinc-500">
                  No knowledge entries yet. Click <span className="font-medium">Add entry</span> to
                  create one.
                </td>
              </tr>
            )}
            {entries.map((e) => (
              <tr key={e.id} className="hover:bg-zinc-50/60 dark:hover:bg-zinc-900/60">
                <td className="max-w-sm px-4 py-3 align-top text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
                  <div className="line-clamp-2">{e.question}</div>
                </td>
                <td className="max-w-md px-4 py-3 align-top text-[12.5px] text-zinc-700 dark:text-zinc-300">
                  <div className="line-clamp-3">{e.answer}</div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex flex-wrap gap-1">
                    {e.tags.length === 0 && <span className="text-[11.5px] text-zinc-400">—</span>}
                    {e.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      e.status === "active"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                        : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
                    }`}
                  >
                    {e.status}
                    {!e.hasEmbedding && (
                      <span
                        title="No embedding on file — only text-index search will find this entry."
                        className="ml-1 text-amber-500"
                        aria-label="No embedding"
                      >
                        ⚠
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(e)}>
                    Edit
                  </Button>
                  <Button variant="ghost-danger" size="sm" onClick={() => setConfirmEntry(e)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadEntries({})}
            disabled={busy}
          >
            {busy ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}

      <KnowledgeDocumentsSection onCorpusChanged={() => void loadTags()} />

      <ConfirmDialog
        open={confirmEntry !== null}
        title="Delete knowledge entry"
        description={
          <>
            Delete <span className="font-medium">“{confirmEntry?.question}”</span>? Flows that
            filter by its tags may no longer find an answer.
          </>
        }
        confirmLabel="Delete entry"
        intent="danger"
        busy={deleting}
        onConfirm={() => {
          if (confirmEntry) void handleDelete(confirmEntry.id);
        }}
        onCancel={() => setConfirmEntry(null)}
      />

      {editing !== null && (
        <KnowledgeEntryDialog
          initial={editing === "new" ? null : editing}
          existingTags={allTags}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Documents — pdf/docx uploads, chunked + embedded server-side
// ===========================================================================

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function KnowledgeDocumentsSection({ onCorpusChanged }: { onCorpusChanged: () => void }) {
  const [documents, setDocuments] = useState<KnowledgeDocumentDTO[]>([]);
  const [busy, setBusy] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tagsInput, setTagsInput] = useState("");
  const [confirmDoc, setConfirmDoc] = useState<KnowledgeDocumentDTO | null>(null);
  const [editingDoc, setEditingDoc] = useState<KnowledgeDocumentDTO | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setDocuments(await api.agentflow.listKnowledgeDocuments());
      setErr(null);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to load documents.");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleUpload(file: File) {
    if (file.size > MAX_UPLOAD_BYTES) {
      setErr(`"${file.name}" is ${formatBytes(file.size)} — the limit is 5 MB.`);
      return;
    }
    setUploading(true);
    setErr(null);
    try {
      const tags = tagsInput
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter((t) => t.length > 0);
      const doc = await api.agentflow.uploadKnowledgeDocument(file, tags);
      setDocuments((prev) => [doc, ...prev]);
      setTagsInput("");
      onCorpusChanged();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleDownload(doc: KnowledgeDocumentDTO) {
    setDownloadingId(doc.id);
    setErr(null);
    try {
      const blob = await api.agentflow.downloadKnowledgeDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleUpdateTags(id: string, tags: string[]) {
    const updated = await api.agentflow.updateKnowledgeDocument(id, tags);
    setDocuments((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    setEditingDoc(null);
    onCorpusChanged();
  }

  async function handleDelete(doc: KnowledgeDocumentDTO) {
    setDeleting(true);
    try {
      await api.agentflow.deleteKnowledgeDocument(doc.id);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
      onCorpusChanged();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to delete document.");
    } finally {
      setDeleting(false);
      setConfirmDoc(null);
    }
  }

  return (
    <section aria-labelledby="kb-docs-heading" className="space-y-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2
            id="kb-docs-heading"
            className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-50"
          >
            Documents
          </h2>
          <p className="mt-0.5 max-w-2xl text-[12.5px] text-zinc-600 dark:text-zinc-400">
            Upload PDF or Word files. Text is extracted, split into chunks, and made searchable to
            your agents alongside Q&amp;A entries. The original file is kept and can be downloaded
            any time.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Tags for upload (optional)"
            aria-label="Tags applied to the next upload, comma-separated"
            className="h-9 w-52 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleUpload(f);
            }}
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? "Processing…" : "Upload document"}
          </Button>
        </div>
      </header>

      {err && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {err}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800/80 dark:bg-zinc-900/40">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-[11.5px] uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
            <tr>
              <th className="px-4 py-2.5 font-medium">File</th>
              <th className="px-4 py-2.5 font-medium">Size</th>
              <th className="px-4 py-2.5 font-medium">Chunks</th>
              <th className="px-4 py-2.5 font-medium">Tags</th>
              <th className="px-4 py-2.5 font-medium">Uploaded</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/60">
            {busy && documents.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && documents.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-[12.5px] text-zinc-500">
                  No documents yet. Upload a .pdf or .docx to add it to your knowledge base.
                </td>
              </tr>
            )}
            {documents.map((d) => (
              <tr key={d.id} className="hover:bg-zinc-50/60 dark:hover:bg-zinc-900/60">
                <td className="max-w-sm px-4 py-3 align-top">
                  <div className="truncate text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
                    {d.filename}
                  </div>
                  <div className="text-[11px] uppercase text-zinc-500">{d.format}</div>
                </td>
                <td className="px-4 py-3 align-top text-[12.5px] text-zinc-700 dark:text-zinc-300">
                  {formatBytes(d.sizeBytes)}
                </td>
                <td className="px-4 py-3 align-top text-[12.5px] text-zinc-700 dark:text-zinc-300">
                  {d.chunkCount}
                  {d.embeddedChunkCount < d.chunkCount && (
                    <span
                      title={`${d.embeddedChunkCount}/${d.chunkCount} chunks have embeddings — the rest are text-index-only.`}
                      className="ml-1 text-amber-500"
                      aria-label="Some chunks have no embedding"
                    >
                      ⚠
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="flex flex-wrap gap-1">
                    {d.tags.length === 0 && <span className="text-[11.5px] text-zinc-400">—</span>}
                    {d.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 align-top text-[12px] text-zinc-500">
                  {new Date(d.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditingDoc(d)}>
                    Edit tags
                  </Button>
                  {d.hasFile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void handleDownload(d)}
                      disabled={downloadingId === d.id}
                    >
                      {downloadingId === d.id ? "Downloading…" : "Download"}
                    </Button>
                  )}
                  <Button variant="ghost-danger" size="sm" onClick={() => setConfirmDoc(d)}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={confirmDoc !== null}
        title="Delete document"
        description={
          <>
            Delete <span className="font-medium">“{confirmDoc?.filename}”</span>? All{" "}
            {confirmDoc?.chunkCount} extracted chunks will be removed, the stored file will be
            deleted, and agents will no longer find its content. This cannot be undone — you would
            need to re-upload the file.
          </>
        }
        confirmLabel="Delete document"
        intent="danger"
        busy={deleting}
        onConfirm={() => {
          if (confirmDoc) void handleDelete(confirmDoc);
        }}
        onCancel={() => setConfirmDoc(null)}
      />

      {editingDoc && (
        <EditDocumentTagsDialog
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onSave={handleUpdateTags}
        />
      )}
    </section>
  );
}

function EditDocumentTagsDialog({
  doc,
  onClose,
  onSave,
}: {
  doc: KnowledgeDocumentDTO;
  onClose: () => void;
  onSave: (id: string, tags: string[]) => Promise<void>;
}) {
  const [tagsInput, setTagsInput] = useState(doc.tags.join(", "));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsedTags = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of tagsInput.split(",")) {
      const t = raw.trim().toLowerCase();
      if (t.length === 0 || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [tagsInput]);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await onSave(doc.id, parsedTags);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to update tags.");
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-doc-tags-title"
    >
      <div className="w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="border-b border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <h2
            id="kb-doc-tags-title"
            className="text-base font-semibold text-zinc-900 dark:text-zinc-50"
          >
            Edit tags
          </h2>
          <p className="mt-0.5 truncate text-[12.5px] text-zinc-500">{doc.filename}</p>
        </div>

        <div className="space-y-3 px-6 py-5">
          <div>
            <label
              htmlFor="kb-doc-tags"
              className="block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Tags
            </label>
            <input
              id="kb-doc-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="comma, separated, tags"
              className="mt-1 block h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
            />
            {parsedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {parsedTags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11.5px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-2 text-[11.5px] text-zinc-500">
              Updating tags also re-tags this document’s {doc.chunkCount} chunks, so flows filtering
              by tag pick up the change.
            </p>
          </div>

          {err && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {err}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save tags"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Edit/Create dialog
// ===========================================================================

function KnowledgeEntryDialog({
  initial,
  existingTags,
  onClose,
  onSave,
}: {
  initial: KnowledgeEntryDTO | null;
  existingTags: string[];
  onClose: () => void;
  onSave: (payload: {
    id?: string;
    question: string;
    answer: string;
    tags: string[];
    status: "active" | "archived";
  }) => Promise<void>;
}) {
  const [question, setQuestion] = useState(initial?.question ?? "");
  const [answer, setAnswer] = useState(initial?.answer ?? "");
  const [tagsInput, setTagsInput] = useState(initial?.tags.join(", ") ?? "");
  const [status, setStatus] = useState<"active" | "archived">(initial?.status ?? "active");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsedTags = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of tagsInput.split(",")) {
      const t = raw.trim().toLowerCase();
      if (t.length === 0 || seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [tagsInput]);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await onSave({
        id: initial?.id,
        question: question.trim(),
        answer: answer.trim(),
        tags: parsedTags,
        status,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  const canSave = question.trim().length > 0 && answer.trim().length > 0 && !busy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kb-dialog-title"
    >
      <div className="w-full max-w-2xl rounded-xl border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
        <div className="border-b border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <h2
            id="kb-dialog-title"
            className="text-base font-semibold text-zinc-900 dark:text-zinc-50"
          >
            {initial ? "Edit knowledge entry" : "Add knowledge entry"}
          </h2>
        </div>

        <div className="space-y-4 px-6 py-5">
          <div>
            <label
              htmlFor="kb-question"
              className="block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Question
            </label>
            <input
              id="kb-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={500}
              className="mt-1 block h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
            />
          </div>
          <div>
            <label
              htmlFor="kb-answer"
              className="block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Answer
            </label>
            <textarea
              id="kb-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              maxLength={4000}
              rows={5}
              className="mt-1 block w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
            />
          </div>
          <div>
            <label
              htmlFor="kb-tags"
              className="block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Tags
            </label>
            <input
              id="kb-tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="comma, separated, tags"
              className="mt-1 block h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
            />
            {parsedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {parsedTags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11.5px] text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
            {existingTags.length > 0 && (
              <p className="mt-2 text-[11.5px] text-zinc-500">
                Existing tags in your KB:{" "}
                <span className="font-mono text-[11px]">{existingTags.join(", ")}</span>
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="kb-status"
              className="block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Status
            </label>
            <select
              id="kb-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as "active" | "archived")}
              className="mt-1 block h-9 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
            >
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {err && (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
              {err}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-zinc-100 px-6 py-4 dark:border-zinc-800">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void submit()} disabled={!canSave}>
            {busy ? "Saving…" : initial ? "Save changes" : "Create entry"}
          </Button>
        </div>
      </div>
    </div>
  );
}
